import React, {useEffect, useMemo, useState} from 'react';

import {skipToken} from '@reduxjs/toolkit/dist/query';
import filter from 'lodash/filter';
import {useDispatch, useSelector} from 'react-redux';
import {browserHistory, WithRouterProps} from 'react-router';

import {DashboardSidebarFoldersList} from 'app/components/general/DashboardPage/DashboardSidebarFoldersList';
import {Filterbar} from 'app/components/general/DashboardPage/Filterbar';
import {LinkPagination} from 'app/components/general/LinkPagination';
import {LinkPaginationRTK} from 'app/components/general/LinkPaginationRTK';
import MainCol from 'app/components/general/MainCol';
import NoResultsMessage from 'app/components/general/NoResultsMessage';
import {StyledSidebarDIV} from 'app/components/general/Sidebar';
import {CurrentUserSelector, useCurrentUserState} from 'app/current-user/api';
import {DashboardTour} from 'app/current-user/components/tour/DashboardTour';
import {
  convertLocationParamsFoldersDocumentsToFetchParams,
  convertLocationParamsToFetchParams,
  convertLocationParamsToFetchParamsExcludeFolder,
  fetchDocumentsForDashboard,
  toggleDocumentsDashboardRelation,
} from 'app/documents/actions';
import {CompactListItem} from 'app/documents/components/CompactListItem';
import {denormalizeDocumentsList} from 'app/documents/datafetchers';
import {createDocument} from 'app/folders/actions';
import {useFolderByHashid, useFolders} from 'app/folders/api';
import {CreateFolderForm} from 'app/folders/components/dashboards/CreateFolder';
import {
  selectIsHidingStackedDashboardFolders,
  toggleIsHidingStackedDashboardFolders,
} from 'app/folders/slice';
import {getFilterLink} from 'app/general/dashboards/DashboardFilterbar';
import {DashboardItemsCounter} from 'app/general/dashboards/DashboardItemsCounter';
import {ResetFilterMessage} from 'app/general/dashboards/ResetFilterMessage';
import {
  BootstrapMinMediumMediaQuery,
  Responsive,
} from 'app/general/mediaqueries';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {SPPage} from 'app/general/structures/SPPage';
import {withLoadingIndicator} from 'app/general/sweet-loading-bar';
import ThreeDotsDropdownMenu from 'app/general/widgets/ThreeDotsDropdownMenu';
import {CreateItemForm} from 'app/kbs/components/KBEditPage/KBTree/KBTreeArticleItem';
import {KBTreeDeleteItemContextProvider} from 'app/kbs/components/KBEditPage/KBTree/KBTreeDeleteItemModal';
import {DocumentsNotificationsProvider} from 'app/notifly/components/DocumentsNotificationsProvider';
import {useSignoffs} from 'app/signoffs/api';
import {useModelChangeSubscription} from 'app/subscriptions';
import {
  DOCUMENT_CREATE_PAGE_URL,
  DOCUMENTS_HOME,
  getDocumentsQRCodeURL,
} from 'app/urls';
import {DEFAULT_PAGE_SIZE} from 'app/utils/constants';
import {gt, ngt} from 'app/utils/i18n';
import {shallowEqual} from 'app/utils/react-utils';
import {useAsyncActions} from 'app/utils/redux';

import {AddItemFormBox} from './AddItemFormBox';
import {DashboardLoadingContent} from './DashboardLoadingContent';
import {DocumentsAddButton} from './DocumentsAddButton';
import {GoFolderUp} from './GoFolderUp';

import {CompactListFolderItem} from '../CompactListFolderItem';

import type {DashboardLocationParamsType} from 'app/documents/types';
import type {ModelChangeMessageType} from 'app/subscriptions';
import type {StoredResponseType} from 'app/utils/redux_v2';
import type {
  BaseDocumentContentType,
  DocumentSerializedType,
  FolderHashidSerializedType,
  FolderSerializedType,
  SearchSerializedType,
} from 'sweet-types';

export const fetchParamsAreDifferent = (
  params: DashboardLocationParamsType,
  nextParams: DashboardLocationParamsType
) => {
  const fetchParams = convertLocationParamsToFetchParamsExcludeFolder(params);
  const nextFetchParams =
    convertLocationParamsToFetchParamsExcludeFolder(nextParams);
  return !shallowEqual(fetchParams, nextFetchParams);
};

type RouterProps = WithRouterProps<unknown, DashboardLocationParamsType>;

type OwnProps = Record<string, unknown>;
type Props = OwnProps & RouterProps;
export function DocumentsDashboard(props: Props) {
  const {query} = props.location;
  const {folder, ...queryWithoutFolder} = query;

  const viewingAll = !fetchParamsAreDifferent(
    Object.freeze({}),
    queryWithoutFolder
  );
  const [addingItem, setAddingItem] = useState<'folder' | 'doc' | null>(null);
  const folderResponse = useFolderByHashid(folder ?? '', {
    skip: folder == null,
  });
  const [selectedFolder, setSelectedFolder] = useState<
    FolderHashidSerializedType | undefined
  >(undefined);

  useEffect(() => {
    if (folder) {
      setSelectedFolder(folderResponse.data);
    } else {
      setSelectedFolder(undefined);
    }
  }, [folder, folderResponse]);

  const teams = selectedFolder != null ? selectedFolder.teams : [];

  const storedFilters = useSelector((state) =>
    state.documents.getIn(['dashboard', 'currentFilters']).toJS()
  );
  const isHidingStackedDashboardFolders = useSelector(
    selectIsHidingStackedDashboardFolders
  );

  const folders = useFolders(
    convertLocationParamsFoldersDocumentsToFetchParams(query),
    {skip: isHidingStackedDashboardFolders}
  );

  useEffect(() => {
    if (fetchParamsAreDifferent(query, storedFilters)) {
      storedFilters.folder = query.folder;
      props.router.replace({
        pathname: location.pathname,
        query: storedFilters,
      });
    }
  }, []);
  const documents: StoredResponseType<DocumentSerializedType> = useSelector(
    (state) =>
      denormalizeDocumentsList(state, convertLocationParamsToFetchParams(query))
  );

  const visibleDocumentIdsWithSignoffs = documents.results
    .filter((document) => document.document.approved_at != null)
    .map((document) => document.object_id);

  const currentUser = useCurrentUserState().data;
  const mySignoffs =
    useSignoffs(
      currentUser == null || visibleDocumentIdsWithSignoffs.length == 0
        ? skipToken
        : {
            page_size: DEFAULT_PAGE_SIZE * 3,
            object_id: visibleDocumentIdsWithSignoffs,
            user_id: currentUser.id,
            filter: 'only_unsigned',
          }
    ).data?.results ?? null;

  const activeRelations = useSelector((state) =>
    state.documents.getIn(['dashboard', 'activeRelations']).toJS()
  );
  const isManager: boolean = useSelector(
    (state) => !!CurrentUserSelector(state)?.data?.is_manager
  );
  const actions = useAsyncActions({
    fetchDocumentsForDashboard,
    withLoadingIndicator,
    createDocument,
  });
  const dispatch = useDispatch();

  const toggleRelation = (relation) =>
    dispatch(toggleDocumentsDashboardRelation(relation));

  const toggleHideFolders = () =>
    dispatch(toggleIsHidingStackedDashboardFolders());
  const refreshData = (query) =>
    actions.withLoadingIndicator(() =>
      Promise.all([
        actions.fetchDocumentsForDashboard(query),
        folders.refetch(),
      ])
    );

  useModelChangeSubscription(
    (message: ModelChangeMessageType, {runWhenAnyModelMatch}) => {
      runWhenAnyModelMatch(
        message,
        [
          'procedure',
          'procedureversion',
          'policy',
          'policyversion',
          'process',
          'processversion',
          'folder',
          'review',
        ],
        () => refreshData(query)
      );
    },
    [query]
  );

  useEffect(() => {
    setAddingItem(null);
    refreshData(query);
  }, [query]);

  const breadcrumbProps = useMemo(
    () => ({
      folder: selectedFolder,
      isDocsNamespace: true,
      isDocsDashboard: true,
    }),
    [selectedFolder?.hashid, folder]
  );

  const resetFilters = () => {
    const {router, location} = props;
    router.replace({
      pathname: location.pathname,
    });
    const link = getFilterLink(
      browserHistory,
      browserHistory.getCurrentLocation(),
      selectedFolder == null
        ? {}
        : {
            folder: selectedFolder.hashid,
          }
    );
    browserHistory.push(link);
  };

  function doCreateDocument(
    value: SearchSerializedType<BaseDocumentContentType>
  ) {
    if (selectedFolder) {
      const payload = {
        document_content_type: value.content_type,
        object_id: value.object_id,
        folder: selectedFolder,
      };
      return actions
        .createDocument(payload)
        .then(() => actions.fetchDocumentsForDashboard(query))
        .then(() => setAddingItem(null));
    }
    return Promise.resolve(true);
  }

  // a single loading flag to rule them all
  // so the page isn't rendered in chunks
  const loading: boolean =
    folderResponse.isLoading || documents.loading || folders.isLoading;

  const actionsButton = (
    <div className="d-flex align-items-center">
      {selectedFolder != null ? (
        <ThreeDotsDropdownMenu
          object={selectedFolder}
          forceMenu={true}
          className="mr-2"
          actionsGetter={(folder: FolderSerializedType) => [
            {
              text: <a>{gt('Show QR Codes')}</a>,
              icon: 'fa fa-qrcode',
              key: 'qrcodes',
              condition: () => true,
              itemProps: {
                href: getDocumentsQRCodeURL(folder.hashid),
                rel: 'noopener noreferrer',
                target: '_blank',
              },
              callback: () => Promise.resolve(null),
            },
          ]}
        />
      ) : null}
      <DocumentsAddButton
        selectedFolderHashid={query.folder}
        invisible={loading}
        onAddExistingDocumentClick={() => setAddingItem('doc')}
        onNewFolderClick={() => setAddingItem('folder')} // user can add a folder if "can_edit" current folder or if user is a super-manager
        canAddFolder={
          selectedFolder != null ? selectedFolder.can_edit : isManager
        } // user can create a document here if they can see the folder (is part of a team)
        // or if user is a super-manager (if no folder is selected)
        canCreateDocument={
          (selectedFolder != null && selectedFolder.can_edit) || isManager
        } // user can add a existing document if can edit the selected folder
        canAddExistingDocument={
          selectedFolder != null && selectedFolder.can_edit
        }
      />
    </div>
  );

  const renderActionsButtonOn = (size: 'small' | 'large') => (
    <Responsive
      breakpoint="md"
      small={size === 'small' ? actionsButton : null}
      large={size === 'large' ? actionsButton : null}
    />
  );

  const foldersToDisplay = filter(folders?.data?.results ?? [], (folder) => {
    if (queryWithoutFolder.search != null) {
      return folder.name.indexOf(queryWithoutFolder.search) !== -1;
    } else {
      return true;
    }
  });
  return (
    <SPPage
      actionArea={
        <>
          {renderActionsButtonOn('large')}
          {renderActionsButtonOn('small')}
        </>
      }
      pageTitle={gt('Documents')}
      breadcrumbProps={breadcrumbProps}
    >
      {({isTeamMember}) => (
        <div className="row">
          <BootstrapMinMediumMediaQuery>
            <StyledSidebarDIV>
              <DashboardSidebarFoldersList />
            </StyledSidebarDIV>
          </BootstrapMinMediumMediaQuery>
          <MainCol>
            <Filterbar
              ct="document"
              resetFilters={resetFilters}
              viewingAll={viewingAll}
              show={{
                search: true,
                sort: true,
                tags: true,
                team: true,
                filters: true,
              }}
            />

            <div className="tour-documents-list">
              {loading ? (
                <DashboardLoadingContent />
              ) : (
                <>
                  <div className="d-flex justify-content-between">
                    {!documents.loading ? (
                      <DashboardItemsCounter
                        text={ngt(
                          '%s document',
                          '%s documents',
                          documents.count
                        )}
                        showSearchHelp={query.search != null}
                      />
                    ) : null}
                    <button
                      className="btn-sm btn btn-link text-muted"
                      onClick={toggleHideFolders}
                    >
                      {isHidingStackedDashboardFolders
                        ? gt('show folders')
                        : gt('hide folders')}
                    </button>
                  </div>

                  <GoFolderUp selectedFolder={selectedFolder} />

                  {addingItem != null ? (
                    <AddItemFormBox
                      label={
                        addingItem === 'doc'
                          ? gt('Add document')
                          : gt('Add new folder')
                      }
                    >
                      {addingItem === 'doc' ? (
                        <CreateItemForm
                          key={'create-doc-' + (folder || '')}
                          onSubmit={doCreateDocument}
                          onCancel={() => setAddingItem(null)}
                          placeholderText={gt(
                            'Select a procedure, process or policy as document...'
                          )}
                          contentTypes={[
                            'procedure',
                            'policy',
                            'proceduregroup',
                          ]}
                        />
                      ) : (
                        <CreateFolderForm
                          autoFocus={true}
                          key={'create-folder-' + (folder || '')}
                          onCreate={() => setAddingItem(null)}
                          onCancel={() => setAddingItem(null)}
                          parent={selectedFolder ?? null}
                          teams={teams}
                        />
                      )}
                    </AddItemFormBox>
                  ) : null}

                  {!isHidingStackedDashboardFolders ? (
                    <KBTreeDeleteItemContextProvider>
                      {foldersToDisplay.map((folder) => (
                        <CompactListFolderItem
                          object={folder}
                          allowedTeams={teams}
                          key={`folder-${folder.id}`}
                          toggleRelation={toggleRelation}
                          activeRelations={activeRelations}
                          refreshData={() => refreshData(query)}
                        />
                      ))}
                    </KBTreeDeleteItemContextProvider>
                  ) : null}

                  {folders?.data ? (
                    <LinkPaginationRTK
                      page={props.location.query.folders_page}
                      isLoading={folders?.isLoading}
                      response={folders?.data}
                      pageParamName={'folders_page'}
                    />
                  ) : null}

                  <DocumentsNotificationsProvider
                    contentType="document"
                    documentsIds={documents.results.map((d) => d.id)}
                  >
                    {(notifications) =>
                      documents.results.map((document) => (
                        <CompactListItem
                          mySignoffs={mySignoffs}
                          sortBy={query.ordering}
                          key={document.id}
                          object={document}
                          currentFolder={folder}
                          contentType={document.document_content_type}
                          activeRelations={activeRelations}
                          toggleRelation={toggleRelation}
                          refreshData={() => refreshData(query)}
                          notifications={notifications[document.document.url]}
                          className="tour-document-item"
                        />
                      ))
                    }
                  </DocumentsNotificationsProvider>

                  {documents.count === 0 ? (
                    <NoResultsMessage
                      className="js-procedures-list js-edit-dropdown"
                      head={
                        viewingAll
                          ? gt('No documents')
                          : gt('No documents found with these filters')
                      }
                    >
                      {(FullHTMLLink) =>
                        viewingAll ? (
                          isTeamMember ? (
                            <FullHTMLLink
                              className="btn btn-primary btn-lg"
                              html_url={
                                DOCUMENT_CREATE_PAGE_URL +
                                (folder != null ? `?folder=${folder}` : '')
                              }
                            >
                              {gt('Create a Document')}
                            </FullHTMLLink>
                          ) : null
                        ) : (
                          <ResetFilterMessage
                            query={query}
                            ct={'document'}
                            resetFilters={resetFilters}
                          />
                        )
                      }
                    </NoResultsMessage>
                  ) : null}

                  {!documents.loading ? (
                    <LinkPagination
                      className="mt-4"
                      page={props.location.query.page}
                      response={documents}
                    />
                  ) : null}
                </>
              )}
            </div>

            <DashboardTour />
          </MainCol>
        </div>
      )}
    </SPPage>
  );
}
export const DocumentsDashboardBreadcrumb = (props: any) => (
  <BreadcrumbItemStructure
    {...props}
    icon="fa-home"
    text={gt('Documents')}
    active={
      props.isDocsDashboard &&
      props.link === DOCUMENTS_HOME &&
      props.folder == null
    }
    link={DOCUMENTS_HOME}
  />
);
export const TextLessDocumentsDashboardBreadcrumb = ({
  isDocsNamespace,
  ...props
}: any) =>
  isDocsNamespace ? null : (
    <BreadcrumbItemStructure {...props} icon="fa-home" link={DOCUMENTS_HOME} />
  );
